<template>
  <div id="device-registration-modal-app">
    <modal
      elem-id="modal_device_registration"
      size="small"
      :close-label="t('Fechar')"
      :save-label="t('Confirmar')"
      :save-disabled-fn="() => !canConfirm"
      :save-fn="handleConfirm"
      :title="t('Registrar Device WhatsApp')"
      :show="isVisible"
      @close="closeModal"
      @dismiss="closeModal">

      <!-- Instructions -->
      <inline-message severity="info" :closable="false">
        <strong>{{ t('Instruções') }}:</strong>
        <ol style="margin: 10px 0 0 0; padding-left: 20px;">
          <li>{{ t('Clique em Confirmar para gerar o QR Code') }}</li>
          <li>{{ t('Escaneie o QR Code com o WhatsApp') }}</li>
          <li>{{ t('Aguarde a confirmação da conexão') }}</li>
        </ol>
      </inline-message>

      <!-- Device Name Display -->
      <box
        type="primary"
        :title="t('Dispositivo')"
        elem-id="boxDeviceName">
        <p><strong>{{ t('Nome do Dispositivo') }}:</strong> {{ deviceName }}</p>
      </box>

      <!-- QR Code Display -->
      <div v-if="qrCodeImage" class="text-center">
        <box
          type="success"
          :title="t('QR Code')"
          elem-id="boxQRCode">
          <div class="text-center">
            <img :src="qrCodeImage" :alt="t('QR Code WhatsApp')" style="max-width: 100%; height: auto;">
            <p class="text-muted mt-2">
              <i class="pi pi-mobile"></i>
              {{ t('Escaneie este QR Code com o WhatsApp') }}
            </p>
          </div>
        </box>
      </div>

      <!-- Loading Spinner -->
      <div v-if="loadingQR" class="text-center">
        <spinner style="width: 2rem; height: 2rem" strokeWidth="4" />
        <p>{{ t('Gerando QR Code...') }}</p>
      </div>

      <!-- Error Message -->
      <inline-message v-if="errorMessage" severity="error" :closable="false">
        {{ errorMessage }}
      </inline-message>

      <!-- Success Message -->
      <inline-message v-if="qrCodeImage && !errorMessage" severity="success" :closable="false">
        <strong>{{ t('Sucesso') }}!</strong> {{ t('Instância criada com sucesso. Escaneie o QR Code com o WhatsApp para conectar o dispositivo.') }}
      </inline-message>
    </modal>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { useDevicesFranquias } from '../../composables/useDevicesFranquias';
import { log } from '../../utils/DebugLogger';

type DeviceId = number;

interface ModalOpenPayload {
  whatsappfranquiaId?: DeviceId;
  deviceName?: string;
}

interface SetupDeviceResponse {
  qrcode?: string;
}

withDefaults(defineProps<{
  usuarioVersao?: number;
}>(), {
  usuarioVersao: 0
});

// Get i18n composable
const { t } = useI18n();

// Get whatsapp franquias composable
const { setupDevice } = useDevicesFranquias();

// Reactive data
const isVisible = ref(false);
const deviceName = ref('');
const qrCodeImage = ref('');
const loadingQR = ref(false);
const errorMessage = ref('');
const currentWhatsappFranquiaId = ref<DeviceId | null>(null);

// Computed properties
const canConfirm = computed(() => {
  return !!deviceName.value && deviceName.value.trim() !== '' && !loadingQR.value && !qrCodeImage.value;
});

// Methods
const open = (whatsappfranquiaId: DeviceId, name?: string): void => {
  log('DeviceRegistrationModal', 'open', 'Opening device registration modal', { whatsappfranquiaId, deviceName: name });

  currentWhatsappFranquiaId.value = whatsappfranquiaId;
  deviceName.value = name || '';
  qrCodeImage.value = '';
  loadingQR.value = false;
  errorMessage.value = '';
  isVisible.value = true;
};

const closeModal = (): void => {
  log('DeviceRegistrationModal', 'closeModal', 'Closing modal');

  isVisible.value = false;
  deviceName.value = '';
  qrCodeImage.value = '';
  loadingQR.value = false;
  errorMessage.value = '';
  currentWhatsappFranquiaId.value = null;
};

const handleConfirm = async (): Promise<void> => {
  log('DeviceRegistrationModal', 'handleConfirm', 'Confirming device registration');

  if (!deviceName.value || deviceName.value.trim() === '') {
    errorMessage.value = t('Nome do dispositivo não informado. Edite o registro e preencha o nome do dispositivo.');
    return;
  }

  if (!currentWhatsappFranquiaId.value) {
    errorMessage.value = t('Erro: WhatsApp Franquia ID não encontrado');
    return;
  }

  loadingQR.value = true;
  errorMessage.value = '';
  qrCodeImage.value = '';

  try {
    const response = (await setupDevice({
      device_id: currentWhatsappFranquiaId.value,
      instance_name: deviceName.value
    })) as SetupDeviceResponse;

    // Evolution API v2 returns base64 QR code directly
    if (response.qrcode) {
      // Add data URI prefix if not already present
      const base64Data = response.qrcode;
      qrCodeImage.value = base64Data.startsWith('data:') ? base64Data : `data:image/png;base64,${base64Data}`;
      errorMessage.value = '';

      // Refresh the table after 2 seconds to show the updated device token
      setTimeout(() => {
        log('DeviceRegistrationModal', 'handleConfirm', 'Refreshing table');
        eventBus.emit('devices-franquias.refresh');
      }, 2000);
    } else {
      errorMessage.value = t('Erro ao gerar QR Code');
    }
  } catch (error) {
    log('DeviceRegistrationModal', 'handleConfirm', 'Error setting up device', error);
    errorMessage.value = t('Erro na comunicação com o servidor');
  } finally {
    loadingQR.value = false;
  }
};

// Event handlers for eventBus
const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('DeviceRegistrationModal', 'handleModalOpen', 'Received open event', data);
  if (data && data.whatsappfranquiaId) {
    open(data.whatsappfranquiaId, data.deviceName);
  }
};

// Lifecycle
onMounted(() => {
  log('DeviceRegistrationModal', 'mounted', 'Component mounted');
  eventBus.on('modal.device-registration.open', handleModalOpen);
  eventBus.on('modal.device-registration.close', closeModal);
});

onBeforeUnmount(() => {
  log('DeviceRegistrationModal', 'beforeUnmount', 'Component unmounting');
  eventBus.off('modal.device-registration.open', handleModalOpen);
  eventBus.off('modal.device-registration.close', closeModal);
});

// Expose methods for parent components
defineExpose({
  open,
  closeModal
});
</script>

<style scoped>
.mt-2 {
  margin-top: 10px;
}
</style>
